// src/components/layout/Header.tsx
import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, MapPin, Calendar } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/80 backdrop-blur-xl shadow-lg shadow-mauve-100/30 border-b border-white/30' 
        : 'bg-gradient-to-b from-white/95 via-white/90 to-white/80 backdrop-blur-lg border-b border-white/30'
    }`}>
      {/* Liquid glass effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/20 to-transparent pointer-events-none" />
      
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #9A7AA0 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }} />
      </div>

      <div className="relative container-custom-responsive">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo with real image */}
          <Link to="/" className="flex items-center space-x-2 md:space-x-3 group relative">
            <div className="relative">
              {/* Logo image with glass effect */}
              <div className="w-10 h-10 md:w-14 md:h-14 rounded-lg md:rounded-xl overflow-hidden bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm border border-white/40 shadow-lg shadow-mauve-100/30 group-hover:shadow-mauve-200/50 transition-all duration-300 flex items-center justify-center">
                <img 
                  src="/gtlogo.png" 
                  alt="Get Nailed by AJ Logo" 
                  className="w-8 h-8 md:w-10 md:h-10 object-contain"
                  onError={(e) => {
                    // Fallback if image doesn't exist
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `
                      <div class="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-gradient-to-br from-mauve-500 to-mauve-600 flex items-center justify-center">
                        <span class="text-white font-bold text-lg md:text-xl">G</span>
                      </div>
                    `;
                  }}
                />
              </div>
              
              {/* Glow effect on hover */}
              <div className="absolute -inset-1 md:-inset-2 bg-gradient-to-r from-mauve-300/20 to-mauve-500/20 rounded-lg md:rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
            </div>
            
            {/* Brand text with gradient */}
            <div className="flex flex-col">
              <h1 className="text-lg md:text-2xl font-bold bg-gradient-to-r from-mauve-700 via-mauve-600 to-mauve-700 bg-clip-text text-transparent">
                GetNailedbyAJ
              </h1>
              <p className="text-xs md:text-sm bg-gradient-to-r from-mauve-500 to-mauve-600 bg-clip-text text-transparent -mt-1 font-medium">
                Premium Beauty Studio
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1 md:space-x-2">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-300 group ${
                  location.pathname === item.path
                    ? 'text-mauve-700 bg-white/50 backdrop-blur-sm shadow-sm'
                    : 'text-mauve-600 hover:text-mauve-700 hover:bg-white/30'
                }`}
              >
                {item.name}
                {location.pathname === item.path && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-mauve-400 to-mauve-500 rounded-full"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                {/* Hover effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-mauve-100/0 via-mauve-100/20 to-mauve-100/0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
            ))}
            
            {/* Book Appointment Button */}
            <a 
              href="https://wa.me/233594197765?text=Hello%20GetNailedbyAJ!%20I'd%20like%20to%20book%20an%20appointment."
              target="_blank"
              rel="noopener noreferrer"
              className="ml-4 px-4 py-2 bg-gradient-to-r from-mauve-500 to-mauve-600 text-white text-sm font-medium rounded-lg hover:from-mauve-600 hover:to-mauve-700 transition-all duration-300 hover:shadow-lg hover:shadow-mauve-300/50 flex items-center gap-2"
            >
              <Calendar size={16} />
              Book Now
            </a>
          </nav>

          {/* Contact info for tablets */}
          <div className="hidden md:flex lg:hidden items-center space-x-3">
            <a 
              href="tel:+233594197765" 
              className="flex items-center space-x-2 text-sm text-mauve-600 hover:text-mauve-700 transition-colors group"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-mauve-100 to-mauve-50 flex items-center justify-center group-hover:from-mauve-200 group-hover:to-mauve-100 transition-all duration-300">
                <Phone size={14} />
              </div>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden relative w-9 h-9 md:w-10 md:h-10 rounded-lg bg-white/50 backdrop-blur-sm border border-white/40 flex items-center justify-center text-mauve-700 hover:text-mauve-500 hover:bg-white/70 transition-all duration-300"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            {/* Button glow */}
            <div className="absolute -inset-1 bg-gradient-to-r from-mauve-300/20 to-mauve-500/20 rounded-lg blur opacity-0 hover:opacity-100 transition-opacity duration-300 -z-10" />
          </button>
        </div>

        {/* Mobile Navigation with glass effect */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden mt-2 mb-4 rounded-2xl bg-gradient-to-b from-white/90 via-white/80 to-white/70 backdrop-blur-xl border border-white/40 shadow-2xl shadow-mauve-100/30 overflow-hidden"
          >
            <div className="p-4 md:p-6">
              {/* Mobile nav items */}
              <div className="space-y-1 md:space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`flex items-center justify-between px-4 py-2.5 md:py-3 rounded-lg transition-all duration-300 ${
                      location.pathname === item.path
                        ? 'bg-gradient-to-r from-mauve-50 to-mauve-100/50 text-mauve-700'
                        : 'text-mauve-600 hover:bg-white/50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="font-medium text-sm md:text-base">{item.name}</span>
                    {location.pathname === item.path && (
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-mauve-400 to-mauve-500" />
                    )}
                  </Link>
                ))}
              </div>

              {/* Mobile contact info */}
              <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-white/40">
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <a 
                    href="tel:+233594197765"
                    className="flex flex-col items-center p-3 rounded-xl bg-gradient-to-br from-white/60 to-white/40 backdrop-blur-sm border border-white/40 hover:border-mauve-200/50 transition-all duration-300 group"
                  >
                    <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-mauve-100 to-mauve-50 flex items-center justify-center mb-1.5 md:mb-2 group-hover:from-mauve-200 group-hover:to-mauve-100 transition-all duration-300">
                      <Phone size={16} className="md:size-[18px]" />
                    </div>
                    <span className="text-xs md:text-sm font-medium text-mauve-700">Call Us</span>
                    <span className="text-xs text-mauve-600">059 419 7765</span>
                  </a>
                  
                  <a
                    href="https://maps.google.com/?q=Westlands+Blvd,+Hatso,+Accra"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center p-3 rounded-xl bg-gradient-to-br from-white/60 to-white/40 backdrop-blur-sm border border-white/40 hover:border-mauve-200/50 transition-all duration-300 group"
                  >
                    <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-mauve-100 to-mauve-50 flex items-center justify-center mb-1.5 md:mb-2 group-hover:from-mauve-200 group-hover:to-mauve-100 transition-all duration-300">
                      <MapPin size={16} className="md:size-[18px]" />
                    </div>
                    <span className="text-xs md:text-sm font-medium text-mauve-700">Location</span>
                    <span className="text-xs text-mauve-600 text-center">Westlands, Accra</span>
                  </a>
                </div>

                {/* Mobile book button */}
                <a
                  href="https://wa.me/233594197765?text=Hello%20GetNailedbyAJ!%20I'd%20like%20to%20book%20an%20appointment."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full mt-4 md:mt-6 px-4 py-3 bg-gradient-to-r from-mauve-500 to-mauve-600 text-white text-sm font-medium rounded-lg hover:from-mauve-600 hover:to-mauve-700 transition-all duration-300 hover:shadow-lg hover:shadow-mauve-300/50 flex items-center justify-center gap-2 group"
                >
                  <Calendar size={18} />
                  Book Appointment
                </a>
              </div>
            </div>

            {/* Mobile menu footer */}
            <div className="px-4 md:px-6 py-3 md:py-4 bg-gradient-to-r from-mauve-50/50 to-mauve-100/30 border-t border-white/40">
              <p className="text-xs text-center text-mauve-600">
                Open 7 Days • 9AM - 7PM
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </header>
  );
};

export default Header;